import { useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { ReportsContext } from '../../../context/report/ReportContext';
import { Menu, ProductCategory } from '../../../models/interfaces';
import ReportDateRange from '../../../components/ui/ReportDateRange';
import Message from '../../../components/ui/Message';
import { downloadCsv } from '../../../utils/csv';

const numberFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

const quantityFormatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 3
});

const unitLabel = (unit: string) => unit == "Unid" ? "Unidad" : unit;

type CategoryGroup = {
    name: string;
    rows: ProductCategory[];
    total: number;
};

export const InvoiceProductCategoryReport = () => {

    const { getInvoiceProductByCategory, getMenusByMerchant } = useContext(ReportsContext);

    const [dateIni, setDateIni] = useState(moment().format('YYYYMMDD'));
    const [dateEnd, setDateEnd] = useState(moment().add(1, 'day').format('YYYYMMDD'));
    const [currency, setCurrency] = useState(188);
    const [appliedCurrency, setAppliedCurrency] = useState(188);
    const [top, setTop] = useState(10);
    const [menuId, setMenuId] = useState(0);
    const [buscar, setBuscar] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<ProductCategory[]>([]);
    const [menus, setMenus] = useState<Menu[]>([]);
    const [error, setError] = useState('');

    useEffect(() => {
        getMenusByMerchant()
            .then(resp => setMenus(resp ?? []))
            .catch(() => setMenus([]));
    }, []);

    const handleCallback = (start, end) => {
        setDateIni(start.format('YYYYMMDD'));
        setDateEnd(moment(end).add(1, 'day').format('YYYYMMDD'));
    };

    const handleFilter = async () => {
        setIsLoading(true);
        try {
            const result = await getInvoiceProductByCategory(
                dateIni,
                dateEnd,
                currency,
                top,
                menuId == 0 ? null : menuId
            );
            setData(result ?? []);
            setAppliedCurrency(currency);
            setHasSearched(true);
        } catch (e) {
            setError('No se pudo generar el reporte, intente de nuevo.');
        } finally {
            setIsLoading(false);
        }
    };

    const filteredData = useMemo(() => {
        const term = buscar.trim().toLowerCase();
        if (!term) return data;
        return data.filter(item =>
            [item.CategoriaNombre, item.Codigo, item.ProductoNombre]
                .some(val => String(val ?? '').toLowerCase().includes(term))
        );
    }, [data, buscar]);

    // Agrupa por categoría respetando el orden en que vienen los datos
    const groups = useMemo<CategoryGroup[]>(() => {
        const map = new Map<string, CategoryGroup>();
        filteredData.forEach(row => {
            const name = row.CategoriaNombre || 'Sin categoría';
            if (!map.has(name)) map.set(name, { name, rows: [], total: 0 });
            const group = map.get(name)!;
            group.rows.push(row);
            group.total += Number(row.TotalFacturado ?? 0);
        });
        return Array.from(map.values());
    }, [filteredData]);

    const totals = useMemo(() => filteredData.reduce((acc, row) => ({
        subtotal: acc.subtotal + Number(row.SubtotalTotal ?? 0),
        descuento: acc.descuento + Number(row.DescuentoTotal ?? 0),
        impuesto: acc.impuesto + Number(row.ImpuestoTotal ?? 0),
        total: acc.total + Number(row.TotalFacturado ?? 0),
    }), { subtotal: 0, descuento: 0, impuesto: 0, total: 0 }), [filteredData]);

    const handleDownload = () => {
        downloadCsv('ReporteProductosFacturados.csv', data.map((inv) => ({
            "Categoría": inv.CategoriaNombre,
            "Código": inv.Codigo,
            "Producto": inv.ProductoNombre,
            "Unidad": unitLabel(inv.UnidadMedida),
            "Cantidad": Number(inv.CantidadTotal).toFixed(2),
            "Subtotal": Number(inv.SubtotalTotal).toFixed(2),
            "Descuento": Number(inv.DescuentoTotal).toFixed(2),
            "Impuesto": Number(inv.ImpuestoTotal).toFixed(2),
            "Total": Number(inv.TotalFacturado).toFixed(2),
        })));
    }

    const renderBody = () => {
        if (isLoading) {
            return (
                <tr><td colSpan={8} className="text-center py-4">
                    <span className="spinner-border spinner-border-sm me-2" /> Generando reporte...
                </td></tr>
            );
        }
        if (!hasSearched) {
            return (
                <tr><td colSpan={8} className="text-center py-4 text-muted">
                    <i className="bi bi-funnel me-2" />Seleccione los filtros y presione "Generar reporte".
                </td></tr>
            );
        }
        if (filteredData.length === 0) {
            return (
                <tr><td colSpan={8} className="text-center py-4 text-muted">
                    {buscar ? 'No se encontraron resultados para la búsqueda.' : 'No hay datos para los filtros seleccionados.'}
                </td></tr>
            );
        }

        const rows: JSX.Element[] = [];
        groups.forEach(group => {
            rows.push(
                <tr key={`cat-${group.name}`} className="table-light">
                    <td colSpan={7}>
                        <i className="bi bi-folder2-open me-2 text-primary" />
                        <span className="fw-bold">{group.name}</span>
                        <span className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis ms-2">{group.rows.length} productos</span>
                    </td>
                    <td className="text-end fw-bold">{numberFormatter.format(group.total)}</td>
                </tr>
            );
            group.rows.forEach((row, idx) => {
                rows.push(
                    <tr key={`${group.name}-${row.Codigo}-${idx}`}>
                        <td className="text-muted" style={{ paddingLeft: 36 }}>{row.Codigo}</td>
                        <td className="fw-medium">{row.ProductoNombre}</td>
                        <td>{unitLabel(row.UnidadMedida)}</td>
                        <td className="text-end">{quantityFormatter.format(row.CantidadTotal)}</td>
                        <td className="text-end">{numberFormatter.format(row.SubtotalTotal)}</td>
                        <td className="text-end">{numberFormatter.format(row.DescuentoTotal)}</td>
                        <td className="text-end">{numberFormatter.format(row.ImpuestoTotal)}</td>
                        <td className="text-end fw-semibold">{numberFormatter.format(row.TotalFacturado)}</td>
                    </tr>
                );
            });
        });
        return rows;
    };

    const currencyLabel = appliedCurrency == 840 ? 'USD' : 'CRC';

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Productos facturados por categoría</h3>
                    <p className="text-muted mb-0">Cantidades y montos facturados por producto, agrupados por categoría.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-outline-secondary" onClick={handleDownload} disabled={data.length === 0}>
                        <i className="bi bi-download me-1" /> Descargar CSV
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                    <div className="row g-2 align-items-end">
                        <div className="col-md-6 col-lg-3">
                            <label className="form-label small text-muted mb-1">Rango de fechas</label>
                            <ReportDateRange onChange={handleCallback} />
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <label className="form-label small text-muted mb-1">Menú</label>
                            <select className="form-select" value={menuId} onChange={(e) => setMenuId(Number(e.target.value))}>
                                <option value={0}>Todos los menús</option>
                                {menus.map((menu) => (
                                    <option key={menu.id} value={menu.id}>{menu.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-6 col-md-4 col-lg-2">
                            <label className="form-label small text-muted mb-1">Moneda</label>
                            <select className="form-select" value={currency} onChange={e => setCurrency(Number(e.target.value))}>
                                <option value={188}>CRC</option>
                                <option value={840}>USD</option>
                            </select>
                        </div>
                        <div className="col-6 col-md-4 col-lg-2">
                            <label className="form-label small text-muted mb-1">Mostrar</label>
                            <select className="form-select" value={top} onChange={e => setTop(Number(e.target.value))}>
                                <option value={10}>10 registros</option>
                                <option value={15}>15 registros</option>
                                <option value={25}>25 registros</option>
                                <option value={0}>Todos</option>
                            </select>
                        </div>
                        <div className="col-md-4 col-lg-2 d-grid">
                            <button className="btn btn-primary" onClick={handleFilter} disabled={isLoading}>
                                <i className="bi bi-bar-chart me-1" /> Generar reporte
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Resultados */}
            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    {hasSearched && data.length > 0 && (
                        <div className="row g-2 mb-3">
                            <div className="col-md-6">
                                <div className="input-group">
                                    <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                    <input
                                        className="form-control"
                                        placeholder="Buscar por categoría, código o producto..."
                                        value={buscar}
                                        onChange={e => setBuscar(e.target.value)}
                                    />
                                    {buscar && (
                                        <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setBuscar('')}>
                                            <i className="bi bi-x-lg" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Código</th>
                                    <th>Producto</th>
                                    <th>Unidad</th>
                                    <th className="text-end">Cantidad</th>
                                    <th className="text-end">Subtotal</th>
                                    <th className="text-end">Descuento</th>
                                    <th className="text-end">Impuesto</th>
                                    <th className="text-end">Total ({currencyLabel})</th>
                                </tr>
                            </thead>
                            <tbody>{renderBody()}</tbody>
                            {!isLoading && filteredData.length > 0 && (
                                <tfoot>
                                    <tr className="table-light fw-semibold">
                                        <td colSpan={4}>Total general</td>
                                        <td className="text-end">{numberFormatter.format(totals.subtotal)}</td>
                                        <td className="text-end">{numberFormatter.format(totals.descuento)}</td>
                                        <td className="text-end">{numberFormatter.format(totals.impuesto)}</td>
                                        <td className="text-end">{numberFormatter.format(totals.total)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {error.length > 0 && (
                <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}
        </div>
    );
};


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
};
